import Reveal from "@/components/site/Reveal";

/**
 * EditorLetter — a signature personal letter from the founder.
 * Styled like a physical letter on textured cream paper, subtly rotated,
 * with a handwritten signature and a coffee-ring detail.
 *
 * This is one of the "wow" moments of the landing — intimate, warm,
 * unmistakably brand-owned.
 */
export default function EditorLetter() {
  return (
    <section
      id="editor-letter"
      data-testid="editor-letter-section"
      className="relative bg-cream-100 py-28 md:py-40 overflow-hidden"
    >
      {/* subtle rice-paper grain over the section */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.05] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial section eyebrow */}
        <Reveal>
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-3 mb-3">
              <span className="h-px w-10 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                Editor&rsquo;s Letter
              </span>
              <span className="h-px w-10 bg-terra" />
            </div>
            <div className="font-magazine italic text-[13px] text-sand-500">
              Nº 01 &middot; aus dem Studio
            </div>
          </div>
        </Reveal>

        {/* The letter · rotated slightly, layered shadow */}
        <Reveal delay={140}>
          <div className="relative">
            {/* washi-tape corners */}
            <div
              aria-hidden="true"
              className="absolute -top-2 left-16 h-6 w-24 bg-brand-rose/70 rotate-[-4deg] z-10 shadow-sm"
              style={{ mixBlendMode: "multiply" }}
            />
            <div
              aria-hidden="true"
              className="absolute -top-2 right-16 h-6 w-24 bg-brand-rose/70 rotate-[4deg] z-10 shadow-sm"
              style={{ mixBlendMode: "multiply" }}
            />

            {/* Paper */}
            <div
              className="relative bg-cream-50 border border-cream-300/60 rotate-[-0.6deg] shadow-[0_30px_60px_-30px_rgba(82,42,37,0.35)] px-8 py-12 md:px-16 md:py-16 lg:px-20 lg:py-20"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='p'><feTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23p)' opacity='0.06'/></svg>\")",
              }}
            >
              {/* Coffee ring — decorative editorial detail */}
              <div
                aria-hidden="true"
                className="absolute -top-6 -right-4 md:-top-8 md:-right-6 w-24 h-24 md:w-28 md:h-28 rounded-full border-[3px] border-terra/25"
                style={{ transform: "rotate(-8deg)" }}
              />
              <div
                aria-hidden="true"
                className="absolute -top-3 -right-1 md:-top-4 md:-right-2 w-16 h-16 rounded-full border-[2px] border-terra/15"
                style={{ transform: "rotate(12deg)" }}
              />

              {/* Date · editorial dateline */}
              <div className="text-[11px] uppercase tracking-[0.28em] text-sand-500 font-semibold mb-1">
                Düsseldorf &middot; Feb 2026
              </div>
              <div className="h-px w-16 bg-terra mb-8" />

              {/* Salutation */}
              <div className="font-magazine text-[1.4rem] md:text-[1.7rem] text-sand-800 leading-tight mb-6">
                Liebe:r Pflege-Held:in,
              </div>

              {/* Body — editorial magazine-style */}
              <div className="space-y-5 text-sand-700 text-[15.5px] md:text-[16px] leading-[1.85] max-w-xl">
                <p>
                  wenn du das hier liest, hast du wahrscheinlich schon mit dem
                  Gedanken gespielt — <em className="text-terra not-italic font-semibold">weiterzumachen</em>.
                  Vielleicht LG1, vielleicht PDL. Vielleicht bist du auch nur neugierig.
                </p>
                <p>
                  Ich weiß, wie sich das anfühlt: der Alltag frisst dich, die
                  Doku frisst dich, und dann sollst du auch noch <span className="italic">frei</span> nachmittags zwei Jahre in
                  eine Schule investieren. Das ergibt keinen Sinn.
                </p>
                <p className="relative">
                  Deshalb haben wir <span className="font-semibold text-sand-800">Dickies-Campus</span> gebaut. Kein Frontalunterricht. Keine
                  monatelangen Wartezeiten. Kein Uni-Ton. Sondern Menschen,
                  die mit dir am gleichen Tisch sitzen und wissen, was ein Montag um
                  6 Uhr in der ambulanten Pflege wirklich bedeutet.
                </p>
                <p>
                  Wir sind ein Bildungsträger — aber wir denken wie ein guter
                  Kaffee: warm, ehrlich, verlässlich. Wenn du magst, ruf einfach an.
                </p>
              </div>

              {/* Signature block */}
              <div className="mt-12 flex items-end justify-between gap-6 flex-wrap">
                <div>
                  {/* Handwritten-style signature */}
                  <div
                    className="text-[2rem] md:text-[2.5rem] text-terra leading-none"
                    style={{
                      fontFamily: "'Caveat', 'Homemade Apple', 'Dancing Script', cursive",
                      transform: "rotate(-2deg)",
                    }}
                  >
                    Markus
                  </div>
                  <div className="mt-3 text-[11px] uppercase tracking-[0.28em] text-sand-500 font-semibold">
                    Markus &middot; Gründer
                  </div>
                </div>

                {/* Editorial stamp */}
                <div
                  aria-hidden="true"
                  className="relative border-[2px] border-terra/60 rounded-full h-20 w-20 flex flex-col items-center justify-center rotate-[-6deg] text-terra"
                >
                  <div className="text-[9px] uppercase tracking-[0.2em] font-bold leading-none">
                    Studio
                  </div>
                  <div className="font-magazine text-[1.05rem] leading-none mt-1">DH</div>
                  <div className="text-[8px] tracking-[0.24em] font-semibold mt-1">2026</div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Editorial footer under letter */}
        <Reveal delay={280}>
          <div className="mt-10 flex items-center justify-center gap-3">
            <span className="h-px w-8 bg-sand-300" />
            <span className="text-[10.5px] uppercase tracking-[0.28em] text-sand-500 font-medium">
              Weiter lesen unten &middot; Kurse ab €490
            </span>
            <span className="h-px w-8 bg-sand-300" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
